import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, setTokenAsync, setRemember } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, refresh } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    // The mobile=1 flag can be either in the search string (?mobile=1) or
    // baked into the hash by Emergent auth. Check both.
    const search = window.location.search || "";
    const isMobile = search.includes("mobile=1") || hash.includes("mobile=1");
    const sessionId = decodeURIComponent(match[1]);
    (async () => {
      try {
        const r = await api.post("/auth/session", { session_id: sessionId });
        // Save session_token as our Bearer credential (works for both web and
        // Android — the backend `resolve_user` accepts either JWT or Emergent
        // session_token in the Authorization header). We always mark this as
        // "remembered" so Google users stay signed in across tab/app restarts,
        // and AWAIT the native Preferences write so a following deep-link
        // redirect doesn't race the async mirror on Android.
        if (r.data.session_token) {
          setRemember(true);
          await setTokenAsync(r.data.session_token);
        }
        setUser(r.data.user);

        if (isMobile) {
          // Bounce back into the native app via the com.notelosh.app:// scheme.
          // Android's intent-filter (see AndroidManifest.xml) brings NoteLosh
          // to the foreground and Capacitor's App plugin fires appUrlOpen with
          // this exact URL — where App.js extracts the token.
          const deepLink = `com.notelosh.app://auth?token=${encodeURIComponent(r.data.session_token)}`;
          window.location.href = deepLink;
          return;
        }

        window.history.replaceState(null, "", "/app");
        await refresh();
        navigate("/app", { replace: true });
      } catch (e) {
        navigate("/login?error=session", { replace: true });
      }
    })();
  }, [navigate, setUser, refresh]);

  return (
    <div className="h-screen w-full flex items-center justify-center bg-[#0A0A0A] text-[#F3F4F6]">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-[#3B82F6]" />
        <p className="text-sm text-[#9CA3AF]">Finishing sign in…</p>
      </div>
    </div>
  );
}
